import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, wsUrl, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { UserAvatar, EfsaneBadge } from "../components/UserAvatar";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "../components/ui/popover";
import {
  Send, Mic, MicOff, Phone, PhoneOff, Search, Link2, ArrowLeft, Youtube, Crown, Volume2, Globe, ExternalLink, Smile, MonitorUp, Coins,
} from "lucide-react";

const ICE = { iceServers: [
  { urls: "stun:stun.l.google.com:19302" },
  { urls: "stun:stun1.l.google.com:19302" },
  { urls: "turn:openrelay.metered.ca:80", username: "openrelayproject", credential: "openrelayproject" },
  { urls: "turn:openrelay.metered.ca:443", username: "openrelayproject", credential: "openrelayproject" },
  { urls: "turn:openrelay.metered.ca:443?transport=tcp", username: "openrelayproject", credential: "openrelayproject" },
] };

const EMOJIS = ["😀","😂","😍","🥳","😎","😭","😡","👍","🙏","🔥","❤️","💀","🎉","👀","🤔","😴","🍿","🎬","🎵","⚽"];
const PREMIUM_EMOJIS = ["🦁","👑","💎","🌟","🚀","🏆","🐉","⚡","🎯","🃏","🥇","🦅"];

let ytApiLoading = null;
function loadYT() {
  if (window.YT && window.YT.Player) return Promise.resolve();
  if (ytApiLoading) return ytApiLoading;
  ytApiLoading = new Promise((resolve) => {
    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    document.body.appendChild(tag);
    window.onYouTubeIframeAPIReady = () => resolve();
  });
  return ytApiLoading;
}

function ytIdFromUrl(url) {
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|v\/)|youtu\.be\/)([\w-]{11})/);
  return m ? m[1] : null;
}

export default function Watch() {
  const { slug } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [room, setRoom] = useState(null);
  const [isHost, setIsHost] = useState(false);
  const [currentVideo, setCurrentVideo] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [messages, setMessages] = useState([]);
  const [coinOverlay, setCoinOverlay] = useState([]);
  const [roomCoinAmt, setRoomCoinAmt] = useState(50);
  const [chatInput, setChatInput] = useState("");
  const [typingUsers, setTypingUsers] = useState([]);
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [browserOpen, setBrowserOpen] = useState(false);
  const [urlInput, setUrlInput] = useState("");
  const [previewUrl, setPreviewUrl] = useState(null);
  const [voiceActive, setVoiceActive] = useState(false);
  const [muted, setMuted] = useState(false);
  const [screenSharing, setScreenSharing] = useState(false);
  const [remoteScreen, setRemoteScreen] = useState(null);

  const wsRef = useRef(null);
  const connIdRef = useRef(null);
  const isHostRef = useRef(false);
  const playerRef = useRef(null);
  const playerHostRef = useRef(null);
  const playerReadyRef = useRef(false);
  const applyingRemoteRef = useRef(false);
  const pendingStateRef = useRef(null);
  const chatScrollRef = useRef();
  const typingTimeoutRef = useRef(null);
  const typingSentRef = useRef(false);

  const localStreamRef = useRef(null);
  const peersRef = useRef({});
  const screenPeersRef = useRef({});
  const screenStreamRef = useRef(null);
  const screenSharingRef = useRef(false);
  const audioContainerRef = useRef(null);
  const voiceActiveRef = useRef(false);

  const isYT = currentVideo && (currentVideo.type === "youtube" || (currentVideo.videoId && currentVideo.type !== "web"));
  const isWeb = currentVideo && currentVideo.type === "web";

  useEffect(() => {
    api.get(`/rooms/${slug}`).then(({ data }) => { setRoom(data); setCurrentVideo(data.current_video); })
      .catch(() => { toast.error("Oda bulunamadı"); navigate("/"); });
    api.get(`/rooms/${slug}/messages`).then(({ data }) => setMessages(data)).catch(() => {});
  }, [slug]);

  const scrollChat = () => {
    const el = chatScrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  };
  useEffect(() => { scrollChat(); }, [messages, typingUsers]);

  // ---------- Player ----------
  const applyPlayback = useCallback((pb, video) => {
    const p = playerRef.current;
    if (!p || !playerReadyRef.current) { pendingStateRef.current = { pb, video }; return; }
    applyingRemoteRef.current = true;
    try {
      if (video && video.videoId) {
        let t = pb?.time || 0;
        if (pb?.playing && pb?.updated_at) t += (Date.now() - new Date(pb.updated_at).getTime()) / 1000;
        if (pb?.playing) p.loadVideoById(video.videoId, t); else p.cueVideoById(video.videoId, t);
      }
    } catch (e) {}
    setTimeout(() => { applyingRemoteRef.current = false; }, 1200);
  }, []);

  const buildPlayer = useCallback((videoId) => {
    loadYT().then(() => {
      if (playerRef.current && playerRef.current.loadVideoById) {
        try { playerRef.current.loadVideoById(videoId); } catch {}
        return;
      }
      if (!playerHostRef.current) return;
      const inner = document.createElement("div");
      playerHostRef.current.innerHTML = "";
      playerHostRef.current.appendChild(inner);
      playerRef.current = new window.YT.Player(inner, {
        videoId,
        width: "100%",
        height: "100%",
        playerVars: { autoplay: 0, rel: 0, modestbranding: 1, playsinline: 1 },
        events: {
          onReady: () => {
            playerReadyRef.current = true;
            if (pendingStateRef.current) { applyPlayback(pendingStateRef.current.pb, pendingStateRef.current.video); pendingStateRef.current = null; }
          },
          onStateChange: (e) => {
            if (applyingRemoteRef.current) return;
            const p = playerRef.current;
            const time = p.getCurrentTime();
            if (e.data === window.YT.PlayerState.PLAYING) sendVideo("play", null, time);
            else if (e.data === window.YT.PlayerState.PAUSED) sendVideo("pause", null, time);
          },
        },
      });
    });
  }, [applyPlayback]);

  useEffect(() => {
    if (isYT && currentVideo?.videoId) buildPlayer(currentVideo.videoId);
  }, [isYT, currentVideo?.videoId, buildPlayer]);

  useEffect(() => {
    if (!isHost) return;
    const t = setInterval(() => {
      const p = playerRef.current;
      if (p && playerReadyRef.current && p.getPlayerState && p.getPlayerState() === 1) sendVideo("play", null, p.getCurrentTime());
    }, 5000);
    return () => clearInterval(t);
  }, [isHost]);

  // ---------- WebSocket ----------
  const send = (obj) => { try { wsRef.current?.send(JSON.stringify(obj)); } catch {} };
  const sendVideo = (action, video, time) => send({ type: "video", action, video, time });

  useEffect(() => {
    let closed = false;
    let reconnectTimer = null;
    const connect = () => {
      const ws = new WebSocket(wsUrl(slug));
      wsRef.current = ws;
      ws.onmessage = (ev) => {
      const msg = JSON.parse(ev.data);
      switch (msg.type) {
        case "init":
          connIdRef.current = msg.conn_id; isHostRef.current = msg.is_host; setIsHost(msg.is_host);
          setParticipants(msg.participants);
          if (msg.current_video) { setCurrentVideo(msg.current_video); if (msg.current_video.videoId) applyPlayback(msg.playback, msg.current_video); }
          break;
        case "participants": setParticipants(msg.participants); if (screenSharingRef.current) { msg.participants.forEach((p) => { if (p.conn_id !== connIdRef.current && !screenPeersRef.current[p.conn_id]) createScreenPeer(p.conn_id); }); } break;
        case "chat": setMessages((m) => m.some((x) => x.id === msg.message.id) ? m : [...m, msg.message]); break;
        case "system": setMessages((m) => [...m, { id: Math.random().toString(), system: true, text: msg.text }]); break;
        case "coin": {
          const ck = Math.random().toString(36).slice(2);
          setCoinOverlay((o) => [...o, { key: ck, amount: msg.amount, from: msg.from }]);
          setTimeout(() => setCoinOverlay((o) => o.filter((x) => x.key !== ck)), 2000);
          setMessages((m) => [...m, { id: ck, system: true, text: `@${msg.from} ${msg.amount} eCoin gönderdi 🪙` }]);
          break;
        }
        case "typing":
          setTypingUsers((u) => msg.typing ? [...new Set([...u, msg.username])] : u.filter((x) => x !== msg.username));
          break;
        case "video":
          if (msg.action === "load") {
            setCurrentVideo(msg.video);
            if (msg.video?.videoId) applyPlayback({ playing: true, time: msg.time, updated_at: new Date().toISOString() }, msg.video);
          } else {
            const p = playerRef.current;
            if (p && playerReadyRef.current) {
              applyingRemoteRef.current = true;
              try {
                if (msg.action === "play") { if (Math.abs(p.getCurrentTime() - msg.time) > 1.5) p.seekTo(msg.time, true); p.playVideo(); }
                else if (msg.action === "pause") { p.seekTo(msg.time, true); p.pauseVideo(); }
              } catch {}
              setTimeout(() => { applyingRemoteRef.current = false; }, 800);
            }
          }
          break;
        case "voice-join": if (voiceActiveRef.current && msg.conn_id) createPeer(msg.conn_id, true); break;
        case "voice-leave": closePeer(msg.conn_id); break;
        case "screen-start": setRemoteScreen({ conn: msg.conn_id, user: msg.user }); break;
        case "screen-stop":
          if (screenPeersRef.current[msg.conn_id]) { screenPeersRef.current[msg.conn_id].close(); delete screenPeersRef.current[msg.conn_id]; }
          setRemoteScreen((r) => (r && r.conn === msg.conn_id ? null : r));
          break;
        case "offer":
          if (msg.kind === "screen") handleScreenOffer(msg.from, msg.data, msg.user);
          else handleOffer(msg.from, msg.data);
          break;
        case "answer":
          if (msg.kind === "screen") { if (screenPeersRef.current[msg.from]) screenPeersRef.current[msg.from].setRemoteDescription(new RTCSessionDescription(msg.data)); }
          else if (peersRef.current[msg.from]) peersRef.current[msg.from].setRemoteDescription(new RTCSessionDescription(msg.data));
          break;
        case "ice":
          if (msg.kind === "screen") { if (screenPeersRef.current[msg.from]) screenPeersRef.current[msg.from].addIceCandidate(new RTCIceCandidate(msg.data)).catch(() => {}); }
          else if (peersRef.current[msg.from]) peersRef.current[msg.from].addIceCandidate(new RTCIceCandidate(msg.data)).catch(() => {});
          break;
        default: break;
      }
    };
      ws.onclose = () => {
        if (closed) return;
        reconnectTimer = setTimeout(connect, 1500);
      };
    };
    connect();
    return () => { closed = true; clearTimeout(reconnectTimer); try { wsRef.current?.close(); } catch {} cleanupVoice(); Object.values(screenPeersRef.current).forEach((pc) => pc.close()); screenStreamRef.current?.getTracks().forEach((t) => t.stop()); };
    // eslint-disable-next-line
  }, [slug]);

  // ---------- WebRTC voice ----------
  const attachRemoteAudio = (connId, stream) => {
    let el = document.getElementById(`audio-${connId}`);
    if (!el) {
      el = document.createElement("audio");
      el.id = `audio-${connId}`;
      el.autoplay = true;
      el.playsInline = true;
      audioContainerRef.current?.appendChild(el);
    }
    el.srcObject = stream;
    el.play?.().catch(() => {});
  };

  const createPeer = (connId, initiator) => {
    if (peersRef.current[connId]) return peersRef.current[connId];
    const pc = new RTCPeerConnection(ICE);
    peersRef.current[connId] = pc;
    if (localStreamRef.current) localStreamRef.current.getTracks().forEach((t) => pc.addTrack(t, localStreamRef.current));
    pc.onicecandidate = (e) => { if (e.candidate) send({ type: "ice", target: connId, data: e.candidate }); };
    pc.ontrack = (e) => attachRemoteAudio(connId, e.streams[0]);
    if (initiator) pc.createOffer().then((o) => { pc.setLocalDescription(o); send({ type: "offer", target: connId, data: o }); });
    return pc;
  };

  const handleOffer = async (from, offer) => {
    if (!voiceActiveRef.current) return;
    const pc = createPeer(from, false);
    await pc.setRemoteDescription(new RTCSessionDescription(offer));
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);
    send({ type: "answer", target: from, data: answer });
  };

  const closePeer = (connId) => {
    const pc = peersRef.current[connId];
    if (pc) { pc.close(); delete peersRef.current[connId]; }
    document.getElementById(`audio-${connId}`)?.remove();
  };

  const cleanupVoice = () => {
    Object.keys(peersRef.current).forEach(closePeer);
    localStreamRef.current?.getTracks().forEach((t) => t.stop());
    localStreamRef.current = null;
    voiceActiveRef.current = false;
  };

  const startVoice = async () => {
    if (!user.is_premium) { toast.error("Sesli sohbet Premium'a özeldir. Efsane ol!"); navigate(`/u/${user.username}`); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true }, video: false });
      localStreamRef.current = stream;
      voiceActiveRef.current = true;
      setVoiceActive(true); setMuted(false);
      send({ type: "voice-join" });
      toast.success("Sesli sohbete katıldın");
    } catch { toast.error("Mikrofon erişimi reddedildi"); }
  };

  const leaveVoice = () => { send({ type: "voice-leave" }); cleanupVoice(); setVoiceActive(false); toast("Sesli sohbetten ayrıldın"); };

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    localStreamRef.current?.getAudioTracks().forEach((t) => { t.enabled = !next; });
    send({ type: "voice-state", muted: next });
  };

  // ---------- Screen share ----------
  const createScreenPeer = (connId) => {
    if (screenPeersRef.current[connId]) return screenPeersRef.current[connId];
    const pc = new RTCPeerConnection(ICE);
    screenPeersRef.current[connId] = pc;
    if (screenStreamRef.current) screenStreamRef.current.getTracks().forEach((t) => pc.addTrack(t, screenStreamRef.current));
    pc.onicecandidate = (e) => { if (e.candidate) send({ type: "ice", kind: "screen", target: connId, data: e.candidate }); };
    pc.createOffer().then((o) => { pc.setLocalDescription(o); send({ type: "offer", kind: "screen", target: connId, data: o }); });
    return pc;
  };

  const handleScreenOffer = async (from, offer, sharer) => {
    let pc = screenPeersRef.current[from];
    if (!pc) { pc = new RTCPeerConnection(ICE); screenPeersRef.current[from] = pc;
      pc.onicecandidate = (e) => { if (e.candidate) send({ type: "ice", kind: "screen", target: from, data: e.candidate }); };
      pc.ontrack = (e) => setRemoteScreen({ conn: from, user: sharer, stream: e.streams[0] });
    }
    await pc.setRemoteDescription(new RTCSessionDescription(offer));
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);
    send({ type: "answer", kind: "screen", target: from, data: answer });
  };

  const startScreenShare = async () => {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
      screenStreamRef.current = stream;
      screenSharingRef.current = true;
      setScreenSharing(true);
      stream.getVideoTracks()[0].onended = stopScreenShare;
      send({ type: "screen-start" });
      participants.forEach((p) => { if (p.conn_id !== connIdRef.current) createScreenPeer(p.conn_id); });
      toast.success("Ekran paylaşımı başladı");
    } catch { toast.error("Ekran paylaşımı iptal edildi"); }
  };

  const stopScreenShare = () => {
    screenSharingRef.current = false;
    setScreenSharing(false);
    Object.values(screenPeersRef.current).forEach((pc) => pc.close());
    screenPeersRef.current = {};
    screenStreamRef.current?.getTracks().forEach((t) => t.stop());
    screenStreamRef.current = null;
    send({ type: "screen-stop" });
  };

  const openGoogle = () => { window.open("https://www.google.com", "_blank", "noopener"); toast("Google açıldı. Filmi bul, sonra 'Ekran Paylaş' ile yansıt."); };

  // ---------- Chat ----------
  const onChatChange = (e) => {
    setChatInput(e.target.value);
    if (!typingSentRef.current) { send({ type: "typing", typing: true }); typingSentRef.current = true; }
    clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => { send({ type: "typing", typing: false }); typingSentRef.current = false; }, 1800);
  };

  const sendChat = async (e) => {
    e.preventDefault();
    const val = chatInput.trim();
    if (!val) return;
    setChatInput("");
    send({ type: "typing", typing: false });
    typingSentRef.current = false;
    try {
      const { data } = await api.post(`/rooms/${slug}/chat`, { text: val });
      setMessages((m) => m.some((x) => x.id === data.id) ? m : [...m, data]);
    } catch {
      toast.error("Mesaj gönderilemedi");
      setChatInput(val);
    }
  };

  const sendRoomCoin = async () => {
    const amount = parseInt(roomCoinAmt, 10);
    if (!amount || amount < 1) { toast.error("Geçerli miktar girin"); return; }
    try {
      await api.post(`/rooms/${slug}/coin`, { amount });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  // ---------- YouTube search ----------
  const doSearch = async (e) => {
    e.preventDefault();
    if (!searchQ.trim()) return;
    setSearching(true);
    try {
      const { data } = await api.get(`/youtube/search?q=${encodeURIComponent(searchQ)}`);
      setSearchResults(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSearching(false); }
  };

  const selectVideo = (v) => {
    const video = { type: "youtube", videoId: v.videoId, title: v.title, thumbnail: v.thumbnail };
    setCurrentVideo(video);
    sendVideo("load", video, 0);
    setSearchOpen(false); setSearchResults([]); setSearchQ("");
  };

  const previewBrowser = (e) => {
    e.preventDefault();
    let url = urlInput.trim();
    if (!url) return;
    if (!/^https?:\/\//.test(url)) url = "https://" + url;
    setPreviewUrl(url);
  };

  const projectUrl = () => {
    let url = previewUrl;
    if (!url) return;
    const ytid = ytIdFromUrl(url);
    let video;
    if (ytid) video = { type: "youtube", videoId: ytid, title: "YouTube Video", thumbnail: `https://i.ytimg.com/vi/${ytid}/hqdefault.jpg` };
    else {
      const direct = /\.(mp4|webm|ogg|mov|m4v)(\?.*)?$/i.test(url);
      video = { type: "web", url, title: url, direct };
    }
    setCurrentVideo(video);
    sendVideo("load", video, 0);
    setBrowserOpen(false); setUrlInput(""); setPreviewUrl(null);
    toast.success("Film herkese yansıtıldı");
  };

  const copyLink = () => { navigator.clipboard.writeText(window.location.href); toast.success("Oda linki kopyalandı!"); };

  if (!room) return <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center text-gray-500">Yükleniyor...</div>;

  const speakingCount = participants.filter((p) => !p.muted).length;

  return (
    <div className="h-screen bg-[#0a0a0a] text-white flex flex-col lg:flex-row overflow-hidden">
      <div ref={audioContainerRef} className="hidden" />

      {/* Main */}
      <div className="flex-none lg:flex-1 flex flex-col min-h-0 min-w-0">
        <div className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 border-b border-white/10 bg-black/40 shrink-0">
          <button data-testid="watch-back" onClick={() => navigate("/")} className="text-gray-400 hover:text-white shrink-0"><ArrowLeft size={20} /></button>
          <div className="min-w-0 flex-1">
            <h1 className="font-bold truncate text-sm sm:text-base">{room.name}</h1>
            <p className="text-xs text-gray-500 truncate">@{room.host?.username} {isHost && <span className="text-[#E50914]">• Host</span>}</p>
          </div>
          <button data-testid="room-share-link-button" onClick={copyLink} className="flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white text-sm px-3 py-2 rounded-md transition shrink-0">
            <Link2 size={16} /> <span className="hidden sm:inline">Paylaş</span>
          </button>
          <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
                <DialogTrigger asChild>
                  <button data-testid="open-youtube-search" className="flex items-center gap-1.5 bg-[#E50914] hover:bg-[#F40612] text-white text-sm px-3 py-2 rounded-md transition shrink-0">
                    <Youtube size={16} /> <span className="hidden sm:inline">Video Seç</span>
                  </button>
                </DialogTrigger>
                <DialogContent className="bg-[#0f0f0f] border-white/10 text-white max-w-2xl p-0 overflow-hidden">
                  <div className="p-4 border-b border-white/10">
                    <form onSubmit={doSearch} className="flex items-center bg-[#121212] border border-white/20 rounded-full overflow-hidden focus-within:border-[#3ea6ff]">
                      <input data-testid="youtube-search-input" autoFocus value={searchQ} onChange={(e) => setSearchQ(e.target.value)} placeholder="Ara"
                        className="flex-1 bg-transparent text-white px-5 py-2.5 focus:outline-none" />
                      <button data-testid="youtube-search-submit" type="submit" className="bg-[#222] hover:bg-[#333] px-5 py-2.5 border-l border-white/20"><Search size={18} /></button>
                    </form>
                  </div>
                  <div className="max-h-[60vh] overflow-y-auto no-scrollbar p-2">
                    {searching && <p className="text-center text-gray-500 py-8">Aranıyor...</p>}
                    {!searching && searchResults.length === 0 && <p className="text-center text-gray-600 py-8">YouTube'da film, dizi, video ara</p>}
                    {searchResults.map((v) => (
                      <button key={v.videoId} data-testid={`yt-result-${v.videoId}`} onClick={() => selectVideo(v)}
                        className="flex gap-3 w-full p-2 rounded-xl hover:bg-white/5 text-left transition">
                        <div className="relative w-40 sm:w-44 shrink-0 aspect-video rounded-lg overflow-hidden bg-black">
                          <img src={v.thumbnail} alt="" className="w-full h-full object-cover" />
                          {v.duration && <span className="absolute bottom-1 right-1 bg-black/85 text-white text-[11px] font-medium px-1 rounded">{v.duration}</span>}
                        </div>
                        <div className="min-w-0 flex-1 py-0.5">
                          <p className="text-sm font-medium leading-snug line-clamp-2" dangerouslySetInnerHTML={{ __html: v.title }} />
                          <div className="flex items-center gap-2 mt-2">
                            {v.channel_thumb && <img src={v.channel_thumb} alt="" className="w-6 h-6 rounded-full" />}
                            <span className="text-xs text-gray-400">{v.channel}</span>
                          </div>
                          {v.views && <p className="text-xs text-gray-500 mt-0.5">{v.views}</p>}
                        </div>
                      </button>
                    ))}
                  </div>
                </DialogContent>
              </Dialog>
        </div>

        {/* Screen */}
        <div className="flex items-center justify-center bg-black relative py-2 lg:flex-1 lg:min-h-0 lg:p-3">
          <div className="absolute inset-0 pointer-events-none overflow-hidden z-20">
            {coinOverlay.map((o) => (
              <div key={o.key} className="gift-float absolute left-1/2 -translate-x-1/2 bottom-16 flex items-center gap-2 bg-black/60 backdrop-blur-sm rounded-full pl-2 pr-4 py-2 border border-[#E50914]/40">
                <img src="/ecoin.png" alt="eCoin" className="h-10 w-10 object-contain drop-shadow-[0_0_16px_rgba(229,9,20,0.8)]" />
                <div className="text-left">
                  <div className="text-white font-black leading-none">{o.amount} eCoin</div>
                  <div className="text-gray-300 text-[11px] mt-0.5">@{o.from} gönderdi</div>
                </div>
              </div>
            ))}
          </div>
          {/* Stable YT host - always mounted, YT injects iframe here; React never touches its children */}
          <div className={isYT ? "w-full max-w-5xl aspect-video max-h-[60vh] lg:max-h-full mx-auto yt-host" : "hidden"}>
            <div ref={playerHostRef} className="w-full h-full" />
          </div>

          {!isYT && (
            <div className="text-center px-6 py-10">
              <Youtube size={56} className="mx-auto text-gray-700 mb-4" />
              <h3 className="text-xl font-bold">Henüz video seçilmedi</h3>
              <p className="text-gray-500 mt-1">'Video Seç' ile bir YouTube videosu başlat.</p>
            </div>
          )}
        </div>

        {/* Voice bar */}
        <div className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 border-t border-white/10 bg-black/60 shrink-0">
          {!voiceActive ? (
            <button data-testid="voice-chat-toggle-button" onClick={startVoice} className={`flex items-center gap-2 text-white font-medium px-4 sm:px-5 py-2.5 rounded-full transition active:scale-95 text-sm ${user.is_premium ? "bg-[#22C55E] hover:bg-[#16a34a]" : "bg-white/10 hover:bg-white/20"}`}>
              {user.is_premium ? <Phone size={18} /> : <span>🔒</span>} <span className="hidden sm:inline">Sesli Sohbete Katıl</span><span className="sm:hidden">Sesli</span>
            </button>
          ) : (
            <>
              <button data-testid="voice-mute-button" onClick={toggleMute}
                className={`flex items-center gap-2 font-medium px-4 sm:px-5 py-2.5 rounded-full transition active:scale-95 text-sm ${muted ? "bg-[#EF4444] hover:bg-red-600" : "bg-white/15 hover:bg-white/25 speaking-ring"} text-white`}>
                {muted ? <MicOff size={18} /> : <Mic size={18} />} <span className="hidden sm:inline">{muted ? "Susturuldu" : "Mikrofon Açık"}</span>
              </button>
              <button data-testid="voice-leave-button" onClick={leaveVoice} className="flex items-center gap-2 bg-[#EF4444] hover:bg-red-600 text-white font-medium px-4 sm:px-5 py-2.5 rounded-full transition active:scale-95 text-sm">
                <PhoneOff size={18} /> <span className="hidden sm:inline">Ayrıl</span>
              </button>
            </>
          )}
          <div className="ml-auto flex items-center gap-1.5 text-xs sm:text-sm text-gray-400">
            <Volume2 size={16} /> {speakingCount} açık
          </div>
        </div>
      </div>

      {/* Sidebar */}
      <div className="w-full lg:w-[380px] shrink-0 border-t lg:border-t-0 lg:border-l border-white/10 bg-[#141414] flex flex-col min-h-0 flex-1 lg:flex-none lg:h-screen">
        <Tabs defaultValue="chat" className="flex flex-col h-full min-h-0">
          <TabsList className="bg-[#181818] border-b border-white/10 rounded-none justify-start px-2 py-1 shrink-0">
            <TabsTrigger value="chat" data-testid="tab-chat">Sohbet</TabsTrigger>
            <TabsTrigger value="participants" data-testid="tab-participants">Katılımcılar ({participants.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="chat" className="flex-1 flex flex-col m-0 min-h-0 data-[state=inactive]:hidden">
            <div ref={chatScrollRef} className="flex-1 overflow-y-auto no-scrollbar p-3 space-y-2 min-h-0">
              {messages.map((m) => {
                if (m.system) return <div key={m.id} className="text-center text-xs text-gray-600 py-1">{m.text}</div>;
                const mine = m.user_id === user.id;
                return (
                  <div key={m.id} className={`flex gap-2 ${mine ? "flex-row-reverse" : "flex-row"}`}>
                    {!mine && <UserAvatar user={m} premium={m.is_premium} className="h-8 w-8 shrink-0 cursor-pointer" onClick={() => navigate(`/u/${m.username}`)} />}
                    <div className={`max-w-[75%] ${mine ? "items-end" : "items-start"} flex flex-col`}>
                      {!mine && (
                        <span className="text-xs text-gray-400 mb-0.5 px-1 flex items-center gap-1.5">
                          <span className="cursor-pointer hover:underline" onClick={() => navigate(`/u/${m.username}`)}>@{m.username}</span>
                          {m.is_premium && <EfsaneBadge />}
                        </span>
                      )}
                      <div className={`px-3.5 py-2 text-sm break-words rounded-2xl ${m.is_premium ? "premium-bubble rounded-br-sm" : mine ? "bg-[#E50914] text-white rounded-br-sm" : "bg-[#2B2B2B] text-gray-100 rounded-bl-sm"}`}>{m.text}</div>
                    </div>
                  </div>
                );
              })}
            </div>
            {typingUsers.length > 0 && (
              <div data-testid="typing-indicator" className="px-4 pb-1 text-xs text-[#E50914] animate-pulse">
                {typingUsers.slice(0, 2).join(", ")} yazıyor...
              </div>
            )}
            <form onSubmit={sendChat} className="flex items-center gap-2 p-3 border-t border-white/10 shrink-0">
              {!isHost && room.host && (
                <Popover>
                  <PopoverTrigger asChild>
                    <button type="button" data-testid="room-coin-button" className="text-[#E50914] hover:opacity-80 transition shrink-0"><Coins size={22} /></button>
                  </PopoverTrigger>
                  <PopoverContent side="top" align="start" className="bg-[#1E1E1E] border-white/10 w-64 p-3">
                    <div className="flex items-center gap-1.5 text-sm text-white font-medium mb-2"><img src="/ecoin.png" alt="" className="h-5 w-5" /> @{room.host?.username}'e eCoin gönder</div>
                    <div className="flex gap-1.5 mb-2">
                      {[10, 50, 100, 500].map((v) => (
                        <button key={v} type="button" data-testid={`room-coin-quick-${v}`} onClick={() => setRoomCoinAmt(v)} className={`flex-1 py-1.5 rounded text-xs font-semibold ${Number(roomCoinAmt) === v ? "bg-[#E50914] text-white" : "bg-white/10 text-gray-300"}`}>{v}</button>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <input data-testid="room-coin-input" type="number" min="1" value={roomCoinAmt} onChange={(e) => setRoomCoinAmt(e.target.value)} className="flex-1 min-w-0 bg-[#2B2B2B] border border-white/10 text-white rounded px-2 py-1.5 text-sm focus:outline-none focus:border-[#E50914]" />
                      <button type="button" data-testid="room-coin-send" onClick={sendRoomCoin} className="bg-[#E50914] hover:bg-[#F40612] text-white font-semibold px-3 rounded text-sm shrink-0">Gönder</button>
                    </div>
                  </PopoverContent>
                </Popover>
              )}
              <Popover>
                <PopoverTrigger asChild>
                  <button type="button" data-testid="emoji-picker-button" className="text-gray-400 hover:text-[#E50914] transition shrink-0"><Smile size={22} /></button>
                </PopoverTrigger>
                <PopoverContent side="top" align="start" className="bg-[#1E1E1E] border-white/10 w-64 p-3">
                  {user.is_premium ? (
                    <>
                      <div className="grid grid-cols-8 gap-1">
                        {[...EMOJIS, ...PREMIUM_EMOJIS].map((em) => (
                          <button key={em} type="button" onClick={() => setChatInput((c) => c + em)} className="text-xl hover:bg-white/10 rounded p-1">{em}</button>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-4">
                      <div className="text-3xl mb-2">🔒</div>
                      <p className="text-sm text-gray-300 mb-3">Emojiler Premium'a özeldir</p>
                      <button type="button" onClick={() => navigate(`/u/${user.username}`)} className="text-xs efsane-badge">✦ Efsane ol, emojileri aç</button>
                    </div>
                  )}
                </PopoverContent>
              </Popover>
              <input data-testid="chat-input" value={chatInput} onChange={onChatChange} placeholder="Mesaj yaz..."
                className="flex-1 bg-[#2B2B2B] border border-white/10 text-white rounded-full px-4 py-2.5 text-sm focus:outline-none focus:border-[#E50914]" />
              <button data-testid="send-message-button" type="submit" className="bg-[#E50914] hover:bg-[#F40612] text-white p-2.5 rounded-full transition shrink-0"><Send size={18} /></button>
            </form>
          </TabsContent>

          <TabsContent value="participants" className="flex-1 overflow-y-auto no-scrollbar m-0 p-3 space-y-2 min-h-0 data-[state=inactive]:hidden">
            {participants.map((p) => (
              <div key={p.conn_id} data-testid={`participant-${p.username}`} className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-white/5">
                <UserAvatar user={p} premium={p.is_premium} className="h-9 w-9 cursor-pointer" onClick={() => navigate(`/u/${p.username}`)} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white flex items-center gap-1.5">@{p.username} {p.is_premium && <EfsaneBadge />} {room.host?.id === p.id && <Crown size={13} className="text-yellow-500" />}</div>
                </div>
                {!p.muted ? <Mic size={16} className="text-[#22C55E]" /> : <MicOff size={16} className="text-gray-600" />}
              </div>
            ))}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
